import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import {
  AppTooltip,
  DataGrid,
  DonutChart,
  Paginate,
  splitTime,
  StyledLink,
} from '@devd-client/devd/components';
import { Box, Flex, IconButton, Text, useDisclosure } from '@chakra-ui/react';
import RecentRuns from './RecentRuns';
import TableBarChart from './TableBarChart';
import TableBarChartModal from './TableBarChartModal';
import { FaExpand } from 'react-icons/fa';
import type {
  PipelineReportData,
  PipelineReportItem,
  StageAvgDuration,
} from '../../helpers/home.types';
import type { TableBarChartDataItem } from './TableBarChart';

interface SprintListProps {
  data: PipelineReportData | undefined;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  loading: boolean;
}

const GetPipelineDetails = ({
  keyOfPipeline,
  value,
}: {
  keyOfPipeline: string;
  value: string;
}) => (
  <Flex align="center" gap={2}>
    <Text fontFamily={'heading'} fontSize="sm" color="text.secondary">
      {keyOfPipeline} :{' '}
    </Text>
    <Text fontFamily={'heading'} fontSize="sm" color="text.primary">
      {value}
    </Text>
  </Flex>
);

const CicdList: React.FC<SprintListProps> = ({
  data,
  currentPage,
  setCurrentPage,
  loading,
}) => {
  const barChartModal = useDisclosure();
  const [currentData, setCurrentData] = useState<{
    data: TableBarChartDataItem[];
    pipeline: string;
  }>();
  const rows: PipelineReportItem[] = useMemo(
    () => data?.pipelineReport || [],
    [data?.pipelineReport]
  );

  const columnHelper = createColumnHelper<PipelineReportItem>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'Details',
        cell: (info) => (
          <Flex direction="column" gap={1}>
            <StyledLink
              path={`/dashboard/cicd/${encodeURIComponent(
                info.row.original?.pipelineDetail?.name
              )}`}
              fontSize="16px"
            >
              {info.row.original?.pipelineDetail?.name}
            </StyledLink>
            <GetPipelineDetails
              keyOfPipeline="Repo"
              value={info.row.original?.pipelineDetail?.repo}
            />
            <GetPipelineDetails
              keyOfPipeline="Type"
              value={info.row.original?.pipelineDetail?.type}
            />
            <GetPipelineDetails
              keyOfPipeline="Env"
              value={info.row.original?.pipelineDetail?.env || 'NA'}
            />
          </Flex>
        ),

        header: 'Pipeline Details',
        size: 200,
      }),
      columnHelper.accessor('totalRuns', {
        cell: (info) => info.getValue(),
        header: 'Total Runs',
        size: 80,
      }),
      columnHelper.accessor('successRate', {
        cell: (info) => (
          <Flex height="140px" width="120px" align="center" justify="center">
            <DonutChart percentage={info.getValue() || 0} />
          </Flex>
        ),
        header: 'Success Rate',
        size: 80,
      }),
      columnHelper.accessor('avgTimeInSecs', {
        cell: (info) => splitTime(info.getValue() / 3600),
        header: 'Avg Time',
        size: 80,
      }),
      columnHelper.accessor('recentRuns', {
        cell: (info) => <RecentRuns info={info} />,
        header: 'Recent Runs',
        size: 180,
      }),
      columnHelper.accessor('stagesAvgDuration', {
        cell: (info) => (
          <Flex height="140px" width="200px" position="relative">
            {info.row.original?.stagesAvgDuration &&
              info.row.original?.stagesAvgDuration?.length && (
                <Box
                  position="absolute"
                  pointerEvents={'auto'}
                  zIndex={1}
                  top={-1}
                  right={-10}
                >
                  <AppTooltip label="Expand" trigger="hover">
                    <IconButton
                      aria-label="expand"
                      size="sm"
                      bg="transparent"
                      onClick={() => {
                        barChartModal.onOpen();
                        setCurrentData({
                          data:
                            info.row.original?.stagesAvgDuration?.map(
                              (item: StageAvgDuration) => ({
                                ...item,
                                value: item.value
                                  ? parseFloat(
                                      parseFloat(String(item.value)).toFixed(2)
                                    )
                                  : 0,
                              })
                            ) || [],
                          pipeline: info.row.original?.pipelineDetail?.name,
                        });
                      }}
                      icon={<FaExpand size={18} />}
                    />
                  </AppTooltip>
                </Box>
              )}
            {info.row.original?.stagesAvgDuration &&
              info.row.original?.stagesAvgDuration?.length && (
                <TableBarChart
                  showLabel={false}
                  data={(
                    info.row.original.stagesAvgDuration as StageAvgDuration[]
                  ).map((item: StageAvgDuration) => ({
                    ...item,
                    value: item.value
                      ? parseFloat(parseFloat(String(item.value)).toFixed(2))
                      : 0,
                  }))}
                />
              )}
          </Flex>
        ),

        header: 'Stages Avg Duration',
        size: 180,
      }),
    ],
    [data?.pipelineReport]
  );

  const handlePageClick = useCallback(
    (page: number) => {
      setCurrentPage(page - 1);
    },
    [currentPage]
  );

  return (
    <Box mt={4}>
      <DataGrid
        data={rows}
        columns={columns}
        showLoader={loading}
        maxH="550px"
        sticky="firstCol"
      />

      <Box marginLeft="-20px" my={'16px'}>
        <Paginate
          currentPage={(data?.currentPage ?? 0) + 1}
          totalPageCount={data?.totalPages ?? 1}
          pageSize={20}
          onPageChange={(page) => handlePageClick(page as number)}
        />
      </Box>
      {barChartModal.isOpen && currentData && (
        <TableBarChartModal
          isOpen={barChartModal.isOpen}
          onClose={barChartModal.onClose}
          data={currentData.data}
          pipeline={currentData.pipeline}
        />
      )}
    </Box>
  );
};

export default CicdList;
